import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode
} from 'react';
import { cn } from '$lib/lib/cn';

export interface PopoverProps {
  trigger: ReactElement;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: 'start' | 'end';
  className?: string;
}

// Minimal popover primitive. Anchors below the trigger, closes on outside
// click and Escape. No portal / no animation — sufficient for filter overflow.

export function Popover({ trigger, children, align = 'start', className }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (e.target instanceof Node && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onTriggerClick = useCallback(() => setOpen((v) => !v), []);

  if (!isValidElement(trigger)) {
    throw new Error('Popover trigger must be a single React element');
  }
  type Triggerable = HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLElement> };
  const triggerEl = trigger as ReactElement<Triggerable>;
  const triggerProps: Triggerable = {
    ...triggerEl.props,
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
    'aria-controls': id,
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      triggerEl.props.onClick?.(e);
      onTriggerClick();
    }
  };

  return (
    <span ref={wrapperRef} className="relative inline-block">
      {cloneElement(triggerEl, triggerProps)}
      {open && (
        <div
          id={id}
          role="dialog"
          className={cn(
            'absolute top-[calc(100%+4px)] z-40 min-w-[280px] rounded-sm border border-rule bg-surface p-3 shadow-elev-3',
            align === 'end' ? 'right-0' : 'left-0',
            className
          )}
        >
          {typeof children === 'function'
            ? (children as (close: () => void) => ReactNode)(() => setOpen(false))
            : children}
        </div>
      )}
    </span>
  );
}
